import axios, { type AxiosError } from 'axios';

// Empty string in development lets the Vite proxy forward /api to the API on :3100.
const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/**
 * The one HTTP client for the Merx API. The session rides on an httpOnly cookie; the
 * X-Requested-With header is the API's CSRF check for every mutating call.
 */
export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'merx' },
  timeout: 30_000,
  withCredentials: true,
});

type ErrorBody = { error?: { code?: string; message?: string } };

export function getErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  return (error as AxiosError<ErrorBody>).response?.data?.error?.code;
}

/** A message safe to show a person, from an API error envelope or a network failure. */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const err = error as AxiosError<ErrorBody>;
    if (!err.response) {
      return err.code === 'ECONNABORTED'
        ? 'The request timed out. Please try again.'
        : 'Could not reach Merx. Check your connection and try again.';
    }
    if (err.response.status === 429) return 'Too many attempts. Please wait a moment.';
    if (err.response.status >= 500) return 'Merx hit an error. Please try again.';
    if (err.response.data?.error?.code === 'READ_ONLY') {
      return 'This is a read-only support view, so changes are turned off.';
    }
    const message = err.response.data?.error?.message;
    if (message && message.length < 200 && !message.includes('Error:')) return message;
  }
  return 'Something went wrong. Please try again.';
}
